import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FooterButton } from 'Core/components';
import CustomPropTypes from 'CustomPropTypes';
import { FileUpload } from '../../core/components';
import { useDocumentUpload } from 'Core/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '../../core/redux/toast/slice';
import { addMachineMaintenanceRecord } from './store/slice';
import { selectMachineSafetyChecklistData } from './store/selectors';
import { fetchMachineSafetyChecklistRecords } from './store/slice';
import ViewAllImagesModal from '../../core/components/viewallimagesmodal/view-all-images-modal';
import WebcamMultiCaptureModal from '../../core/components/modal/webcam-multi-capture-modal';
import SegmentedImagesModal from '../../core/components/modal/segmentedimagesmodal/segmented-images-modal';

const initData = {
  id: 0,
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  question: '',
  answer: '',
  status: '',
  comments: '',
  serviceRecords: [],
  safetyChecklist: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const ignoreKeys = [
  'pictures',
  'comments',
  'personnelRecords',
  'serviceRecords',
  'downTime',
  'workOrderNumber', // Server generates via NextModuleSequence on insert
  'question',
  'answer',
  'status',
  'safetyChecklist',
  'machineId',
];

const MaintenanceRecordAddForm = ({
  data,
  machineId,
  onChanges,
  onCloseModal,
  maintenanceTypeOptions,
}) => {
  const [pictureDocuments, setPictureDocuments] = useState([]);
  const isViewMode = Boolean(data?.id != 0);
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(isViewMode ? data : initData);
  const safetyData = useSelector(selectMachineSafetyChecklistData);

  const [uploadedFiles, setUploadedFiles] = useState(
    formData.pictures
      .split(',')
      .filter(file => file.trim() !== '')
      .map((file, index) => ({ id: index + 1, fileName: file, relativePath: file })),
  );

  const [maintenancePersonnels, setMaintenancePersonnels] = useState(formData.personnelRecords);

  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [showImageViewerModal, setShowImageViewerModal] = useState(false);
  const [showManageImagesModal, setShowManageImagesModal] = useState(false);
  const [maintenanceRecordId, setMaintenanceRecordId] = useState(0);
  const [processedImageSegments, setProcessedImageSegments] = useState([]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      id: prev.id ?? 0,
      safetyChecklist: prev.safetyChecklist ?? [],
      workOrderNumber: prev.workOrderNumber ?? '',
      machineId: machineId,
      date: prev.date || new Date().toISOString().split('T')[0],
      personnelName: prev.personnelName ?? '',
    }));

    dispatch(fetchMachineSafetyChecklistRecords(machineId));
  }, [machineId, dispatch]);

  // Process image segments when data prop changes
  useEffect(() => {
    if (data?.imageSegments) {
      const processed = processImageSegments(data.imageSegments);
      setProcessedImageSegments(processed);
    } else {
      setProcessedImageSegments([]);
    }

    const mid = data?.maintenanceId ?? data?.id;
    if (mid != null && mid !== '' && Number(mid) !== 0) {
      setMaintenanceRecordId(Number(mid));
    }
  }, [data]);

  const handleFilesChange = files => {
    setUploadedFiles(files);
  };

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const updateDateTimeField = (index, fieldName, value) => {
    const updatedPersonnels = maintenancePersonnels.map((personnel, i) => {
      if (i === index) {
        return {
          ...personnel,
          [fieldName]: value,
        };
      }
      return personnel;
    });

    setMaintenancePersonnels(updatedPersonnels);
  };

  const fileCategories = [
    { id: 1, value: 'Purchase Order' },
    { id: 2, value: 'Receipt' },
    { id: 3, value: 'Service Report' },
    { id: 4, value: 'Inspection Report' },
    { id: 5, value: 'Calibration Certificate' },
    { id: 6, value: 'Other' },
  ];

  /** Upload key segment: no client WO until server assigns one */
  const uploadKeySuffix =
    formData.workOrderNumber?.trim() ||
    (maintenanceRecordId ? `ID${maintenanceRecordId}` : `M${machineId}_NEW`);

  const [selectedFiles, setSelectedFiles] = useState(formData.serviceRecords);
  const [tempFile, setTempFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const { uploadDocuments, loading } = useDocumentUpload();

  const handleFileUpload = e => {
    const file = e.target.files[0];
    if (file && selectedCategory) {
      const selectedCategoryObj = fileCategories.find(cat => cat.value === selectedCategory);
      setTempFile({
        file,
        fileCategoryId: selectedCategoryObj?.id || 0,
        fileCategory: selectedCategory,
      });
    }
  };

  const handleAdd = async () => {
    if (!tempFile) return;
    const docFormData = new FormData();
    docFormData.append('file', tempFile.file);
    docFormData.append('fileCategoryId', `MACHINEMAINTENANCERECORD${uploadKeySuffix}`);
    docFormData.append('uploadCategory', 'MACHINEMAINTENANCERECORD');

    try {
      const result = await uploadDocuments(docFormData);

      const item = {
        documentName: tempFile.fileCategory,
        documentPath: result.relativePath,
      };
      setSelectedFiles([...selectedFiles, item]);

      setTempFile(null);
      setSelectedCategory('');
    } catch (error) {
      dispatch(
        showToast({
          message: 'File upload failed. Try again.',
          type: 'danger',
        }),
      );
    }
  };

  const handleAddPersonnel = async () => {
    const newFile = {
      id: 0,
      name: '',
      startTime: '',
      endTime: '',
    };

    const newFiles = [...maintenancePersonnels, newFile];
    setMaintenancePersonnels(newFiles);
  };

  const [safetyChecklistForms, setSafetyChecklistForms] = useState([]);

  const updateSafetyField = (index, fieldName, value) => {
    const updated = [...safetyChecklistForms];
    const updatedItem = { ...updated[index] };
    updatedItem[fieldName] = value;
    updatedItem['isDirty'] = true;
    updated[index] = updatedItem;
    setSafetyChecklistForms(updated);
  };

  useEffect(() => {
    if (safetyData.length > 0) {
      setSafetyChecklistForms(safetyData);
    }
  }, [safetyData]);

  const valid = () => {
    if (
      !formData ||
      Object.keys(formData).some(key => {
        if (ignoreKeys.includes(key)) {
          return false;
        }
        const v = formData[key];
        if (v === 0 || v === false) {
          return false;
        }
        return !v;
      })
    ) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const handleClose = () => {
    onCloseModal();
  };

  const onSave = async () => {
    for (const tFile of uploadedFiles.filter(file => file.preview)) {
      const docFormData = new FormData();
      docFormData.append('file', tFile.file);
      docFormData.append('fileCategoryId', `MACHINEMAINTENANCEPICTURES${uploadKeySuffix}`);
      docFormData.append('uploadCategory', `MACHINEMAINTENANCEPICTURES`);

      try {
        const result = await uploadDocuments(docFormData);

        formData.pictures += `,${result.relativePath}`;
      } catch (error) {
        console.error('File upload failed:', error);
      }
    }

    const isNewRecord = !formData.id || Number(formData.id) === 0;
    const payload = {
      ...formData,
      id: formData.id || 0,
      maintenanceId: formData.id || 0,
      userId: localStorage.getItem('UserId'),
      serviceRecords: selectedFiles,
      personnelRecords: maintenancePersonnels,
      safetyChecklist: safetyChecklistForms,
      workOrderNumber: isNewRecord ? null : formData.workOrderNumber,
    };
    
    console.log('Saving maintenance record with payload:', payload);
    if (isViewMode) {
      dispatch(addMachineMaintenanceRecord(payload));
    } else {
      dispatch(addMachineMaintenanceRecord(payload));
    }
  };

  const getDocumentName = fileUrl => {
    let fileName = '';
    if (fileUrl != undefined) {
      const encodedFileName = fileUrl.split('/').pop();
      const decoded = decodeURIComponent(encodedFileName);
      const lastUnderscore = decoded.lastIndexOf('_');
      const extensionIndex = decoded.lastIndexOf('.');

      if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
        fileName = decoded?.slice(0, lastUnderscore) + decoded?.slice(extensionIndex);
      } else {
        fileName = decoded;
      }
    }

    return fileName;
  };

  const handlePictureUpload = uploadedFiles => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const docs = uploadedFiles.map(file => ({
      name: file.fileName ?? 'Captured Image',
      path: file.filePath ?? file.relativePath ?? '',
    }));

    setPictureDocuments(docs);

    setFormData(prev => ({
      ...prev,
      pictures: docs.map(doc => doc.path).join(','),
    }));
  };

  const handleMaintenanceRecordSaved = (maintenanceId, savedWorkOrderNumber) => {
    console.log('Maintenance record saved with ID:', maintenanceId);
    setMaintenanceRecordId(maintenanceId);
    setFormData(prev => ({
      ...prev,
      id: maintenanceId,
      ...(savedWorkOrderNumber != null && String(savedWorkOrderNumber).trim() !== ''
        ? { workOrderNumber: savedWorkOrderNumber }
        : {}),
    }));
  };

  // Process image segments from API response (aligned with mold-maintenance file shape for SegmentedImagesModal)
  const processImageSegments = imageSegments => {
    if (!imageSegments || !Array.isArray(imageSegments)) return [];

    const norm = seg => ({
      id: seg.id ?? seg.Id,
      record: seg.record ?? seg.Record,
      category: seg.category ?? seg.Category,
      imageUrl: seg.imageUrl ?? seg.ImageUrl,
      segment: seg.segment ?? seg.Segment ?? 'General',
      title: seg.title ?? seg.Title ?? '',
      description: seg.description ?? seg.Description ?? '',
    });

    const picturesSegments = imageSegments
      .map(norm)
      .filter(
        seg =>
          String(seg.record) === 'Pictures' &&
          String(seg.category) === 'Machine-Maintenance-Records',
      );

    const source = picturesSegments.length > 0 ? picturesSegments : imageSegments.map(norm);

    return source.map(segment => {
      const url = segment.imageUrl || '';
      const baseName =
        segment.title?.trim() ||
        (url ? url.split('/').pop()?.split('?')[0] : '') ||
        `Image_${segment.id ?? 'new'}`;
      return {
        id: segment.id,
        name: baseName,
        path: url,
        filePath: url,
        fileName: baseName,
        imageUrl: url,
        segment: segment.segment || 'General',
        slotName: segment.segment || 'General',
        record: segment.record || 'Pictures',
        title: segment.title,
        description: segment.description,
      };
    });
  };

  const documentsFromPicturesField =
    formData.pictures && processedImageSegments.length === 0
      ? formData.pictures
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map((path, idx) => {
            const name = path.split('/').pop()?.split('?')[0] || `Image_${idx}`;
            return {
              id: `pictures_${idx}`,
              name,
              path,
              filePath: path,
              fileName: name,
              imageUrl: path,
              segment: 'General',
              slotName: 'General',
              record: 'Pictures',
            };
          })
      : [];

    const parsedPicturesDocuments = formData.pictures
    ? formData.pictures.split(',').map(path => ({
        name: path.split('/').pop(),
        path,
      }))
    : [];
    
  return (
    <>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Work Order Number</label>
            <input
              type="text"
              className="form-control"
              readOnly
              disabled={false}
              placeholder="Assigned when the record is saved"
              value={formData.workOrderNumber || ''}
              onChange={e => updateFormField('workOrderNumber', e.target.value)}
            />
            {!formData.workOrderNumber?.trim() && (
              <small className="text-muted">Generated on the server; you do not need to enter a number.</small>
            )}
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              disabled={true}
              value={formData.date}
              onChange={e => updateFormField('date', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              rows={5}
              cols={5}
              disabled={!isViewMode}
              className="form-control"
              placeholder="Enter maintenance description"
              defaultValue={''}
              value={formData.description}
              onChange={e => updateFormField('description', e.target.value)}
            />
          </div>
        </div>

        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Maintenance Type <span className="text-danger">*</span>
            </label>
            <select
              value={formData.maintenanceType}
              onChange={e => updateFormField('maintenanceType', e.target.value)}
              className="form-control"
              required
            >
              {maintenanceTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Personnel Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              disabled={!isViewMode}
              value={formData.personnelName}
              placeholder="Current User"
              onChange={e => updateFormField('personnelName', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {formData.maintenanceType == 3 && (
        <div>
          {safetyChecklistForms.map((safetyCheckItem, index) => (
            <div className="row border" key={index}>
              <h5 className="mb-3 mt-3">Safety Check Item #{index + 1}</h5>
              <div className="col-lg-6 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>
                    Task/Question <span className="text-danger">*</span>
                  </label>
                  <textarea
                    rows={5}
                    cols={5}
                    className="form-control"
                    placeholder="Enter the safety check task or question"
                    defaultValue={''}
                    value={safetyCheckItem.question}
                    onChange={e => updateSafetyField(index, 'question', e.target.value)}
                  />
                </div>
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <div className="input-blocks">
                  <label>
                    Status <span className="text-danger">*</span>
                  </label>

                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Basic radio toggle button group"
                  >
                    <input
                      type="radio"
                      className="btn-check"
                      id={`btnradioYes${index}`}
                      name={`btnradioStatus${index}`}
                      checked={safetyCheckItem.status === true}
                      value="Yes"
                      onChange={e => updateSafetyField(index, 'status', true)}
                    />
                    <label className="btn btn-outline-primary" htmlFor={`btnradioYes${index}`}>
                      Yes
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      id={`btnradioNo${index}`}
                      name={`btnradioStatus${index}`}
                      checked={safetyCheckItem.status === false}
                      value="No"
                      onChange={e => updateSafetyField(index, 'status', false)}
                    />
                    <label className="btn btn-outline-primary" htmlFor={`btnradioNo${index}`}>
                      No
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-lg-12 col-md-12 col-sm-12">
                <div className="input-blocks">
                  <label>
                    Comments <span className="text-danger">*</span>
                  </label>
                  <textarea
                    rows={5}
                    cols={5}
                    className="form-control"
                    placeholder="Enter any comments or observations"
                    defaultValue={''}
                    value={safetyCheckItem.comments}
                    onChange={e => updateSafetyField(index, 'comments', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>Pictures</label>
            <FileUpload onChange={handleFilesChange} value={uploadedFiles} />
          </div>
        </div>
      </div> */}
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>Pictures</label>
      <div className="d-flex gap-2">
  <button
    type="button"
    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
    onClick={() => setShowWebcamModal(true)}
  >
    <i className="feather-camera" />
    Capture Image
  </button>

  {(parsedPicturesDocuments.length > 0 || processedImageSegments.length > 0) && (
    <button
      type="button"
      className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
      onClick={() => setShowManageImagesModal(true)}
    >
      <i className="feather-image" />
      Manage Images
    </button>
  )}
</div>

          </div>
        </div>
      </div>

      <div className="row border">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Service Completed Documents</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th colSpan="4">
                        <div className="d-flex gap-3 align-items-center">
                          <select
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                            className="form-control"
                          >
                            <option value="">Select document type</option>
                            {fileCategories.map(category => (
                              <option key={category.id} value={category.value}>
                                {category.value}
                              </option>
                            ))}
                          </select>
                          <input type="file" onChange={handleFileUpload} className="form-control" />
                          <button
                            type="button"
                            className="btn btn-info"
                            onClick={handleAdd}
                            disabled={loading || !tempFile}
                          >
                            {loading ? 'Uploading...' : 'Add'}
                          </button>
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <th>Document Type</th>
                      <th>Document Name</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFiles.map((file, index) => (
                      <tr key={index}>
                        <td className="">{file.documentName}</td>
                        <td className="">{getDocumentName(file.documentPath)}</td>
                        <td className="border p-1">
                          <a
                            href="javascript:void(0);"
                            onClick={() => {
                              const newFiles = selectedFiles.filter((_, i) => i !== index);
                              setSelectedFiles(newFiles);
                              if (onChanges) onChanges(newFiles);
                            }}
                            className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                          >
                            <i className="feather-trash"></i>
                          </a>
                        </td>
                        <td className="border p-1">
                          <a
                            href="javascript:void(0);"
                            className="btn btn-icon btn-sm btn-soft-success rounded-pill"
                          >
                            <i className="feather-download"></i>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row border">
        <div className="col-lg-12">
          <div className="input-blocks mt-2">
            <div className="d-flex justify-content-between">
              <h5>Maintenance Personnel</h5>

              <button type="button" className="btn btn-info" onClick={handleAddPersonnel}>
                + Add Personnel
              </button>
            </div>
            <div>
              {maintenancePersonnels.map((personnel, index) => (
                <div className="d-flex gap-2 align-items-center" key={index}>
                  <div className="input-blocks w-100">
                    <label>Personnel Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Personnel Name"
                      value={personnel.personnelName}
                      onChange={e => {
                        const updated = [...maintenancePersonnels];
                        updated[index].personnelName = e.target.value;
                        setMaintenancePersonnels(updated);
                      }}
                    />
                  </div>
                  <div className="input-blocks">
                    <label>Start time</label>
                    <input
                      type="datetime-local"
                      value={personnel.startTime}
                      onChange={e => updateDateTimeField(index, 'startTime', e.target.value)}
                      className="form-control"
                    />
                  </div>
                  <div className="input-blocks">
                    <label>End time</label>
                    <input
                      type="datetime-local"
                      value={personnel.endTime}
                      onChange={e => updateDateTimeField(index, 'endTime', e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>Down Time</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.downTime}
              placeholder="Auto-calculated based on status"
              onChange={e => updateFormField('downTime', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-8"></div>
        <div className="col-12 col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Maintenance Record"
            onSave={onSave}
            onCancel={handleClose}
          />
        </div>
      </div>

{showWebcamModal && (
  <WebcamMultiCaptureModal
    show={showWebcamModal}
    onClose={() => setShowWebcamModal(false)}
    onSubmit={handlePictureUpload}
    context="maintenance-records"
    buttonLabel="Pictures"
    maintenanceData={formData}
    onMaintenanceSaved={handleMaintenanceRecordSaved}
  />
)}

     {showImageViewerModal && (
        <ViewAllImagesModal
          show={showImageViewerModal}
          onClose={() => setShowImageViewerModal(false)}
          documents={parsedPicturesDocuments}
        />
      )}

                           {showManageImagesModal && (
          <SegmentedImagesModal
            show={showManageImagesModal}
            onClose={() => setShowManageImagesModal(false)}
            documents={
              processedImageSegments.length > 0
                ? processedImageSegments
                : pictureDocuments.length > 0
                  ? pictureDocuments
                  : documentsFromPicturesField
            }
            moldInfoID={0}
            buttonLabel="Pictures"
            maintenanceId={maintenanceRecordId}
            context="maintenance-records"
          />
        )}
        
        {/* Debug info */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ display: 'none' }}>
            <p>Debug Info:</p>
            <p>processedImageSegments: {JSON.stringify(processedImageSegments)}</p>
            <p>pictureDocuments: {JSON.stringify(pictureDocuments)}</p>
            <p>maintenanceRecordId: {maintenanceRecordId}</p>
            <p>data.imageSegments: {JSON.stringify(data?.imageSegments)}</p>
          </div>
        )}
    </>
  );
};

MaintenanceRecordAddForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape(CustomPropTypes.machines),
  onChanges: PropTypes.func,
};

export default MaintenanceRecordAddForm;
